import { useState, useEffect } from 'react';
import { useUpdateAgent } from '../../hooks/useAgents';
import { Agent } from '../../services/agents.service';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';

interface EditAgentModalProps {
  agent: Agent;
  isOpen: boolean;
  onClose: () => void;
}

export default function EditAgentModal({ agent, isOpen, onClose }: EditAgentModalProps) {
  // Helper to extract skill names from the skills array
  const getSkillsString = (skills: any[] | undefined) => {
    if (!skills || skills.length === 0) return '';
    return skills
      .map(skill => typeof skill === 'string' ? skill : skill.skillGroup?.name || '')
      .filter(Boolean)
      .join(', ');
  };

  const [formData, setFormData] = useState({
    firstName: agent.firstName,
    lastName: agent.lastName,
    skills: getSkillsString(agent.skills),
    languages: agent.languages?.join(', ') || '',
    timezone: agent.timezone,
    status: agent.status,
    targetUtilization: (agent.targetUtilization || 0).toString(),
  });

  const updateAgent = useUpdateAgent();

  useEffect(() => {
    setFormData({
      firstName: agent.firstName,
      lastName: agent.lastName,
      skills: getSkillsString(agent.skills),
      languages: agent.languages?.join(', ') || '',
      timezone: agent.timezone,
      status: agent.status,
      targetUtilization: (agent.targetUtilization || 0).toString(),
    });
  }, [agent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateAgent.mutateAsync({
        id: agent.id,
        agent: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          skills: formData.skills.split(',').map((s) => s.trim()).filter(Boolean),
          languages: formData.languages.split(',').map((l) => l.trim()).filter(Boolean),
          timezone: formData.timezone,
          status: (formData.status === 'ACTIVE' || formData.status === 'INACTIVE') ? formData.status : 'ACTIVE',
          targetUtilization: parseInt(formData.targetUtilization),
        },
      });

      onClose();
    } catch (error) {
      console.error('Error updating agent:', error);
      alert('Failed to update agent. Please try again.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Agent"
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={updateAgent.isPending}
          >
            Save Changes
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            required
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
          />
          <Input
            label="Last Name"
            required
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
          />
        </div>

        <Select
          label="Status"
          required
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value as Agent['status'] })}
          options={[
            { value: 'ACTIVE', label: 'Active' },
            { value: 'INACTIVE', label: 'Inactive' },
          ]}
        />

        <Input
          label="Skills"
          placeholder="customer-service, technical-support, sales"
          helperText="Comma-separated list of skills"
          value={formData.skills}
          onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
        />

        <Input
          label="Languages"
          placeholder="English, Spanish, French"
          helperText="Comma-separated list of languages"
          value={formData.languages}
          onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
        />

        <Select
          label="Timezone"
          required
          value={formData.timezone}
          onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
          options={[
            { value: 'America/New_York', label: 'Eastern Time (ET)' },
            { value: 'America/Chicago', label: 'Central Time (CT)' },
            { value: 'America/Denver', label: 'Mountain Time (MT)' },
            { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
            { value: 'Europe/London', label: 'London (GMT)' },
            { value: 'Europe/Paris', label: 'Paris (CET)' },
            { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
          ]}
        />

        <Input
          label="Target Utilization (%)"
          type="number"
          min="0"
          max="100"
          required
          value={formData.targetUtilization}
          onChange={(e) => setFormData({ ...formData, targetUtilization: e.target.value })}
          helperText="Expected percentage of productive time"
        />
      </form>
    </Modal>
  );
}
